import React from "react";
import { Button, Section, Text } from "@origin-digital/ods-core";
import { IconCancelCircle, IconSearch } from "@origin-digital/ods-icons";
import { useNavigate } from "react-router-dom";
import { Autocomplete as MuiAutocomplete, PaperProps } from "@mui/material";
import { BASE_PATH } from "../../helpers/router.helper";
import {
  StyledPopper,
  Container,
  StyledPaper,
  StyledTextField,
  StyledSearchIcon,
  CancelButtonContainer,
  StyledCancelIconButton,
} from "./styles";

type Props = {
  setInputValue: (value: string) => void;
  onFocusChange: (state: boolean) => void;
  inputValue: string;
  suggestions: string[];
};

const PaperComponent = ({ children, ...props }: PaperProps) => {
  return (
    <Section backgroundColor="white" paddingY="none">
      <StyledPaper {...props}>{children}</StyledPaper>
    </Section>
  );
};

export const Autocomplete = ({
  suggestions,
  inputValue,
  setInputValue,
  onFocusChange,
}: Props) => {
  const navigate = useNavigate();

  const handleChange = (_e, value, reason) => {
    if (reason === "createOption" || reason === "selectOption") {
      setInputValue(value);
      navigate(`${BASE_PATH}/search?term=${encodeURIComponent(value)}`);

      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document?.activeElement?.blur?.();
        }
      }, 0);
    }
  };

  return (
    <Container>
      <MuiAutocomplete
        options={[
          inputValue,
          ...suggestions.filter((item) => item !== inputValue),
        ]}
        renderInput={({
          InputLabelProps,
          InputProps,
          inputProps,
          ...props
        }) => (
          <StyledTextField
            {...props}
            muiProps={{
              InputProps: {
                ...InputProps,
                startAdornment: <StyledSearchIcon color="grey300" />,
                endAdornment: (
                  <StyledCancelIconButton
                    data-id="clear-text-field"
                    open={inputValue?.length > 0}
                    onMouseDown={(e) => {
                      // prevent loss of focus for the input field when the close button is clicked
                      e.preventDefault();
                    }}
                    onClick={() => {
                      setInputValue("");
                    }}
                  >
                    <IconCancelCircle color="neutralLight" />
                  </StyledCancelIconButton>
                ),
              },
              inputProps: {
                ...inputProps,
                "data-id": "search-input",
              },
              InputLabelProps,
            }}
            onFocus={() => onFocusChange(true)}
            onBlur={() => onFocusChange(false)}
            hideHelperTextSpace
            id="autocomplete-input"
            placeholder="What are you looking for?"
            startIcon={<IconSearch color="grey300" />}
            variant="outlined"
          />
        )}
        PopperComponent={StyledPopper}
        PaperComponent={PaperComponent}
        renderOption={(props, option) => (
          <Text
            {...props}
            data-id="search-suggestion"
            // disable selection styling and behaviour
            aria-selected="false"
            key={option}
            component="button"
          >
            {option}
            {option === inputValue ? "..." : ""}
          </Text>
        )}
        inputValue={inputValue}
        freeSolo
        onChange={handleChange}
        onInputChange={(_e, value) => setInputValue(value || "")}
      />
      <CancelButtonContainer>
        {/* No onclick handler needed as clicking the button deselects the input field */}
        <Button
          variant="text"
          color="secondaryB"
          size="small"
          data-id="cancel-input-focus"
        >
          Cancel
        </Button>
      </CancelButtonContainer>
    </Container>
  );
};
